import { Dialog } from "@headlessui/react";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { ModalFoterBox } from "../../components/box/Box";
import Button from "../../components/button/Button";
import { Modal, ModalProp } from "../../components/modal/Modal";
import { RadioButton } from "../../components/radiobutton/RadioButton";
import { useInput } from "../../hooks/useInput";
import TableList, { GridCol, GridRow } from "../../components/table/TableList";
import { SearchKeys, KeyValues } from "../../model/SearchKeys";
import { SearchKeyDeleteModal } from "./SearchKeyDeleteModal";
import companiesQuery from "../../firestore/Companies.query";
import { useDispatch } from "react-redux";
import { AlertValue, alertOpen } from "../../features/alert.slice";
import userQuery from "../../firestore/User.query";
import { TextField } from "../../components/fields/Fields";
import { ConstMessage } from "../../config/const";
import { Spiner } from "../../components/spinner/Spiner";
import { generateRandomDigits } from "../../utility/CommonUtility";

type FormData = {
  no: number;
  id?: string;
} & {
  [key in `searchName${number}`]: string;
} & {
  [key in `sort${number}`]: number;
};

export type SearchKeysEditModalProps = {
  searchKey?: SearchKeys[];
  tenantId: string;
  setIsUpdateSearchKeys: React.Dispatch<React.SetStateAction<boolean>>;
}

export const SearchKeysEditModal = ({
  open,
  toggle,
  closeOnClickOutside,
  searchKey,
  tenantId,
  setIsUpdateSearchKeys,
}: ModalProp & SearchKeysEditModalProps) => {

  const dispatch = useDispatch();
  const [isLoad, setIsLoad] = useState(false)
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [deleteKeyValues, setDeleteKeyValues] = useState<string>();
  const [deleteKeyNo, setDeleteKeyNo] = useState<number>(0);
  const [displaySearchKey, setDisplaySearchKey] = useState<FormData[]>([]);
  const [tableResetSort, setTableResetSort] = useState<(() => void) | null>(null);
  const [viewSearchKey, setViewSearchKey] = useState<SearchKeys[]>(searchKey ?? []);

  useEffect(() => {
    setViewSearchKey(searchKey ?? []);
  }, [searchKey]);


  const onClickDeleteButton = (deleteKeyValues:string,itemNo:number) => {
     if(deleteKeyValues != "") {
      deleteDialogToggle();
     } else {
      onClickDelete(itemNo);
     }
  };

  // 削除ダイアログ開閉
  const deleteDialogToggle = () => {
    setIsDialogOpen(!isDialogOpen);
  };

  const selectItems = [
    {id: "searchKey1", title: "検索キー1", value: "0"},
    {id: "searchKey2", title: "検索キー2", value: "1"},
    {id: "searchKey3", title: "検索キー3", value: "2"}
  ];

  const displayList = useInput("0");

  const transformToDisplaySearchKey = (input: { id?: string | undefined, sort: number | string; searchName: string }[]): FormData[] => {
    return input.map((item, index) => {
      const result: { [key: string]: string | number } = {
        no: index + 1,
        id: item.id ? item.id : '',
      };
      result[`searchName${index}`] = item.searchName;
      result[`sort${index}`] = item.sort;
      return result as FormData;
    });
  };
  
  useEffect(() => {
    const setDisplayData = async() => {
      let updatedDisplaySearchKey: FormData[] = [];
      let keyValueData = [];
      const searchKeys = await companiesQuery.getSearchKeys(tenantId);
      setViewSearchKey(searchKeys);
      
      if (displayList.value === "0") {
        const userSearchKey1 = searchKeys && searchKeys.length !== 0 
          ? searchKeys.filter((item) => item.name === 'userSearchKey1')[0]?.keyValues ?? []
          : [];
        updatedDisplaySearchKey = transformToDisplaySearchKey(userSearchKey1);
        keyValueData = userSearchKey1;
      } else if (displayList.value === "1") {
        const userSearchKey2 = searchKeys && searchKeys.length !== 0 
          ? searchKeys.filter((item) => item.name === 'userSearchKey2')[0]?.keyValues ?? []
          : [];
        updatedDisplaySearchKey = transformToDisplaySearchKey(userSearchKey2);
        keyValueData = userSearchKey2;
      } else if (displayList.value === "2") {
        const userSearchKey3 = searchKeys && searchKeys.length !== 0 
          ? searchKeys.filter((item) => item.name === 'userSearchKey3')[0]?.keyValues ?? []
          : [];
        updatedDisplaySearchKey = transformToDisplaySearchKey(userSearchKey3);
        keyValueData = userSearchKey3;
      }
      if (tableResetSort) {
        tableResetSort();
      }
      setDisplaySearchKey(updatedDisplaySearchKey);
    }
    setDisplayData();

  }, [displayList.value]);

  type EditableSearchKey = {
    no: number;
    searchName: string;
    sort: number | string;
    id?: string;
  };
  type SortOrder = "asc" | "desc";

  const [editableSearchKey, setEditableSearchKey] = useState<EditableSearchKey[]>([]);
  const { control, handleSubmit, trigger, getValues, reset, formState: { errors } } = useForm<FormData>({
    defaultValues: editableSearchKey.reduce((acc, item, index) => {
      acc[`searchName${index}`] = item.searchName || ''; 
      acc[`sort${index}`] = (item.sort as number) || 0;
      return acc;
    }, {} as FormData),
  });
  const renumberKeys = (keys: EditableSearchKey[]): EditableSearchKey[] => {
    return keys.map((key, index) => ({
      ...key,
      no: index + 1,
    }));
  };

  useEffect(() => {
    const initialData = displaySearchKey.length > 0
      ? displaySearchKey.map((item, index) => {
          const searchName = item[`searchName${index}`] || "";
          return {
            no: item.no,
            searchName: searchName,
            sort: typeof item[`sort${index}`] === "number" ? item[`sort${index}`] : "",
            id: item.id ? item.id : "",
          };
        })
      : [];

    setEditableSearchKey(initialData);
  
    const defaultValues = initialData.reduce((acc, item, index) => {
      acc[`searchName${index}`] = item.searchName || "";
      const sortValue = (typeof item.sort === "string"
        ? (item.sort.trim() === "" ? "" : parseInt(item.sort, 10) || 0)
        : (item.sort === undefined || item.sort === null ? 0 : item.sort));
      acc[`sort${index}`] = sortValue;
      return acc;
    }, {} as any);
  
    const defaultValuesData = Object.keys(defaultValues).reduce((acc, key) => {
      acc[key] = defaultValues[key] === "" ? "" : defaultValues[key];
      return acc;
    }, {} as any);
    reset(defaultValuesData);
    reset(defaultValues);
  }, [displaySearchKey, reset, displayList.value]);

  const addEmptyRow = () => {
    const newRow: EditableSearchKey = {
      no: editableSearchKey.length + 1,
      searchName: "",
      sort: "" as unknown as number,
      id: "",
    };
    const currentValues = getValues();
    const updatedKeys = [...editableSearchKey, newRow];
    setEditableSearchKey(updatedKeys);
  
    const defaultValues = updatedKeys.reduce((acc, item, index) => {
      acc[`searchName${index}`] = currentValues[`searchName${index}`] ?? item.searchName;
      acc[`sort${index}`] = currentValues[`sort${index}`] ?? (index === 0 ? "" : "" as unknown as number); 
      return acc;
    }, {} as FormData);
  
    reset(defaultValues, { keepDirty: true });
  };
  const handleCustomSort = (key: string, order: SortOrder) => {
    if (!["searchName", "sort"].includes(key)) {
      console.error(`Invalid key: ${key}`);
      return;
    }
    const sortedKeys = [...editableSearchKey].sort((a, b) => {
      const valA = parseInt(a[key as keyof EditableSearchKey] as string, 10);
      const valB = parseInt(b[key as keyof EditableSearchKey] as string, 10);
      return order === "asc" ? valA - valB : valB - valA;
    });
    const renumberedKeys = renumberKeys(sortedKeys);

    setDisplaySearchKey(
      renumberedKeys.map((item, index) => {
        const result: { [key: string]: string | number } = {
          no: index + 1,
          id: item.id ? item.id : '',
        };
        result[`searchName${index}`] = item.searchName;
        result[`sort${index}`] = item.sort;
        return result as FormData;
      })
    );
  };

  const searchKeyCols: GridCol[] = [
    {
      field: "no",
      headerName: "No",
      style: "w-8",
      sort: false,
    },
    {
      field: "searchKey",
      headerName: "設定値",
      style: "w-80",
      sort: false,
    },
    {
      field: "sort",
      headerName: "表示順",
      style: "w-16",
      sort: true,
    },
    {
      field: "button",
      headerName: (
        <div className={"py-2"} style={{ display: "flex", justifyContent: "flex-end", marginRight: "10px" }}>
          <Button
            onClick={addEmptyRow}
            children="追加"
            disabled={editableSearchKey.some(item => item.id === '') || editableSearchKey.length >= 30}
          />
        </div>
      ),
      style: "w-2/12",  
      sort: false,
    },
  ];

  const searchKeyRows: GridRow[] = editableSearchKey.map<GridRow>((item, index) => ({
    key: `row-${index}`,
    rowData: {
      no: (
        <div className="w-8" style={{textAlign: 'right'}}>
          {item.no}
        </div>
        ),
      searchKey: (
        <div className="h-16 mt-5">
          <Controller
            control={control}
            name={`searchName${index}`}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                type={"text"}
                className={" "}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
              />
            )}
            rules={{
              validate: (value) => {
                const duplicates = editableSearchKey.filter(
                  (key, i) => key.searchName === value && i !== index
                );
                if (!value) {
                  return ConstMessage.ERR_NOT_INPUT;
                }
                else if (duplicates.length > 0) {
                  return ConstMessage.ERR_SAME_VALUE;
                }
                return true;
              },
            }}
          />
        </div>
      ),
      sort: (
        <div className="h-16 mt-5">
          <Controller
            control={control}
            name={`sort${index}`}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                type={"text"}
                className={"w-16"}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                errMsgWhitespaceWrap={"whitespace-pre"}
              />
            )}
            rules={{
              validate: (value) => {
                const strValue = typeof value === "string" ? value : String(value);
                const numValue = parseInt(strValue, 10);
                if (strValue == "") {
                  return ConstMessage.ERR_NOT_INPUT;
                }
                else if (!/^\d+$/.test(strValue)) {
                  return ConstMessage.ERR_NON_NUMERIC;
                }
                else if (numValue < 0 || numValue > 999) {
                  return ConstMessage.ERR_DISPLAY_ORDER_RANGE;
                }
                return true;
              },
            }}
          />
        </div>
      ),
      button: (
        <div className="flex ml-6">
          {!item.id ? (
            <Button
            className="mt-1 mr-2"
            onClick={() => validateAndSubmit(item.no, item.id, index)}
          >
            登録
          </Button>
          ) : (
            <>
              <Button
                className="mt-1 mr-2"
                onClick={() => validateAndSubmit(item.no, item.id, index)}
              >
                更新
              </Button>
              <Button
                className="mt-1 mr-2"
                color="red"
                onClick={() => {
                  setDeleteKeyNo(item.no);
                  setDeleteKeyValues(item.searchName);
                  onClickDeleteButton(item.searchName,item.no);
                }}
              >
                削除
              </Button>
            </>
          )}
        </div>
      ),
    },
  }));

  const validateAndSubmit = async (itemNo:number, id:string | undefined, index:number) => {
    // 特定のフィールドを手動でバリデーション
    const isValid = await trigger([`searchName${index}`, `sort${index}`]);
    if (isValid) {
      const searchName = getValues(`searchName${index}`);
      const sort = getValues(`sort${index}`);
      onClickSubmit(itemNo, id, searchName, sort.toString(10));
    }
  };

  //検索キー追加編集
  const onClickSubmit = async (itemNo:number, id: string | undefined, editSearchName:string, editSort:string) => {
    setIsLoad(true);
    let updateSearchKey: SearchKeys[] = [];
    let KeysName: string = "";
    let searchKeysName: string = "";
    let searchKeyId: string | null = null;
    let editFlg = false;
  
    if (displayList.value === "0") {
      updateSearchKey = viewSearchKey?.filter((item) => item.name === "userSearchKey1") ?? [];
      KeysName = "userSearchKey1";
      searchKeysName = "searchKey1";
    } else if (displayList.value === "1") {
      updateSearchKey = viewSearchKey?.filter((item) => item.name === "userSearchKey2") ?? [];
      KeysName = "userSearchKey2";
      searchKeysName = "searchKey2";
    } else if (displayList.value === "2") {
      updateSearchKey = viewSearchKey?.filter((item) => item.name === "userSearchKey3") ?? [];
      KeysName = "userSearchKey3";
      searchKeysName = "searchKey3";
    }

    try {
      if (updateSearchKey.length === 0) {
        // 検索キーが未登録（データ件数が0）の場合
        const newKeyValues: KeyValues[] = [];
        if (editSearchName && editSort !== undefined && editSort !== null) {
          newKeyValues.push({
            id: generateRandomDigits(8),
            searchName: editSearchName,
            sort: parseInt(editSort, 10),
          });
        }

        const newSearchKey: SearchKeys = {
          id: "",
          name: KeysName,
          keyValues: newKeyValues,
          createAt: new Date(),
          updateAt: new Date(),
        };
  
        const createdSearchKey = await companiesQuery.createSearchKey(tenantId, newSearchKey);
        setIsUpdateSearchKeys(true)
        const searchKeys = await companiesQuery.getSearchKeys(tenantId);
        setViewSearchKey(searchKeys);
        const updatedSearchKeyData = searchKeys.filter((item) => item.name === KeysName)[0]?.keyValues; 
        setDisplaySearchKey(transformToDisplaySearchKey(updatedSearchKeyData));
  
        dispatch(alertOpen(AlertValue("検索キーを保存しました。", "success")));
        setIsLoad(false);
        return;
      }
      
      searchKeyId = updateSearchKey[0].id;
      // 登録後のkeyValuesを生成
      let keyValues: KeyValues[] = [];
      let oldName = "";
      let newName = "";
      let keyValuesLength = updateSearchKey[0].keyValues.length;
      let generateId = generateRandomDigits(8);
      if (keyValuesLength === 0) {
        // keyValuesフィールドが空の場合（追加）
        const keyValue: KeyValues = {
          id: generateId,
          searchName: editSearchName,
          sort: parseInt(editSort, 10),
        };
        keyValues.push(keyValue);
      } else {
        keyValues = updateSearchKey[0].keyValues;
        const oldKeyValues = [...keyValues];
        if (id === "") {
          // 追加する場合
          const keyValue: KeyValues = {
            id: generateId,
            searchName: editSearchName,
            sort: parseInt(editSort, 10),
          };
          keyValues.push(keyValue)
        } else {
          // 編集の場合
          const updateData = keyValues.map((value) => 
            value.id === id
              ? {...value, searchName: editSearchName, sort: parseInt(editSort, 10)}
              : value
          );
          keyValues = updateData;
          const oldData = oldKeyValues.filter((value) => value.id === id);
          oldName = oldData[0].searchName;
          newName = editSearchName;
          editFlg = true;
        }
      }
  
      // 検索キーを更新
      keyValues.sort((a, b) => a.sort - b.sort);
      await companiesQuery.updateSearchKeys(tenantId, searchKeyId!, keyValues);
      setIsUpdateSearchKeys(true)
      const searchKeys = await companiesQuery.getSearchKeys(tenantId);
      setViewSearchKey(searchKeys);

      if (editFlg) {
        // ユーザの検索キーを更新
        const user = await userQuery.getUserBySearchName(tenantId, searchKeysName, oldName);
        if (user) {
          await userQuery.updateUserSearchName(tenantId, user.id, searchKeysName, newName);
        }
      }
      
      const allValues = getValues();
      let allSearchKeys = editableSearchKey.map((item, i) => {
        let itemId;
        if (editFlg === false && allValues[`searchName${i}`] === editSearchName) {
          itemId = generateId;
        } else {
          itemId = item.id;
        }

        return {
          no: item.no,
          searchName: allValues[`searchName${i}`],
          sort: typeof allValues[`sort${i}`] === 'string' && allValues[`sort${i}`] !== '' ? parseInt(allValues[`sort${i}`], 10) : allValues[`sort${i}`],
          id: itemId ? itemId : '',
        }
      });
      setDisplaySearchKey(transformToDisplaySearchKey(allSearchKeys));

      dispatch(alertOpen(AlertValue(`設定値「${editSearchName}」の検索キーを保存しました。`, "success")));
    } catch (error) {
      console.error("更新/追加エラー: ", error);
      dispatch(alertOpen(AlertValue("エラーが発生しました。", "danger")));
    } finally {
      setIsLoad(false);
    }
  };

  //検索キー削除
  const onClickDelete = async (deleteKeyNo:number) => {
    const deleteKeyValue = editableSearchKey.filter((item) => item.no === deleteKeyNo);
    setIsLoad(true);
    let updateSearchKey: SearchKeys[] = [];
    let searchKeysName: string = "";
    if (displayList.value === "0") {
      updateSearchKey = viewSearchKey?.filter((item) => item.name === 'userSearchKey1') ?? [];
      searchKeysName = '1';
    } else if (displayList.value === "1") {
      updateSearchKey = viewSearchKey?.filter((item) => item.name === 'userSearchKey2') ?? [];
      searchKeysName = '2';
    } else if (displayList.value === "2") {
      updateSearchKey = viewSearchKey?.filter((item) => item.name === 'userSearchKey3') ?? [];
      searchKeysName = '3';
    }
    setIsUpdateSearchKeys(true)

    if(updateSearchKey[0] && deleteKeyValue[0]) {
      await companiesQuery.deleteSearchKeys(tenantId, updateSearchKey[0].id, deleteKeyValue[0].searchName);
      setIsUpdateSearchKeys(true)
      await userQuery.deleteSearchKeys(tenantId, searchKeysName, deleteKeyValue[0].searchName);
    }
    const searchKeys = await companiesQuery.getSearchKeys(tenantId);
    setViewSearchKey(searchKeys);

    let allValues = getValues();
    let allSearchKeys = editableSearchKey.map((item, i) => ({
      no: item.no,
      searchName: allValues[`searchName${i}`],
      sort: typeof allValues[`sort${i}`] === 'string' && allValues[`sort${i}`] !== '' ? parseInt(allValues[`sort${i}`], 10) : allValues[`sort${i}`],
      id: item.id ? item.id : '',
    }));
    allSearchKeys = allSearchKeys.filter((value) => value.id !== deleteKeyValue[0].id);
    setDisplaySearchKey(transformToDisplaySearchKey(allSearchKeys));
    setIsLoad(false);
    if (isDialogOpen === true) {
      deleteDialogToggle();
    }
    dispatch(alertOpen(AlertValue(`設定値「${deleteKeyValue[0].searchName}」の検索キーを削除しました。`, "success")));

  };

  return (
        <Modal
      open={open}
      toggle={toggle}
      closeOnClickOutside={closeOnClickOutside}
    >
      <Dialog.Overlay>
        {
          <>
            {isLoad === true ?
              <Spiner /> : (
                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white  text-left shadow-xl transition-all sm:w-full max-w-screen-lg ">
                  <div className="px-2 pb-4 sm:p-4">
                    {/* 削除確認モーダル */}
                    {isDialogOpen && (<SearchKeyDeleteModal
                      children={undefined}
                      open={isDialogOpen}
                      toggle={deleteDialogToggle}
                      closeOnClickOutside={false}
                      handle={() => onClickDelete(deleteKeyNo)}
                      deleteKeyValues={deleteKeyValues}
                      isLoad={isLoad}
                    />)}
                    <div className="text-center sm:mt-3">
                      <Dialog.Title
                        as="h3"
                        className="text-left text-lg font-medium leading-6 text-gray-900"
                      >
                        検索キー設定
                      </Dialog.Title>
                      <h3 className="text-left text-sm text-gray-500">検索キー1、検索キー2、検索キー3で使用される項目の値を追加、編集、削除できます</h3>
                      <div className="overflow-y-auto">
                        <div className="px-2 pt-8">
                          <div className="flex justify-center items-center">
                            <RadioButton
                              children={undefined}
                              name={"displayList"}
                              label={"表示中のリスト"}
                              radioButtonItem={selectItems}
                              value={displayList}
                              vertical
                            />
                          </div>
                          <div className={"pt-6 mx-10 max-h-[calc(100vh_-_16rem)]"}>
                            <TableList
                              footer={false}
                              gridCol={searchKeyCols}
                              gridRow={searchKeyRows}
                              onCustomSort={handleCustomSort}
                              displayListValue={displayList.value}
                              setTableResetSort={setTableResetSort}
                              lineHeight={"py-1"}
                            />
                          </div>
                        </div>

                      </div>
                    </div>
                  </div>
                  <ModalFoterBox>
                    <Button
                      className="mx-2 mt-1 text-indigo-500"
                      outline
                      onClick={toggle}
                      children={"閉じる"}
                    />
                  </ModalFoterBox>
                </Dialog.Panel>
              )}
          </>
        }
      </Dialog.Overlay>
    </Modal>
  )
}